import { Component, Inject, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface SaasStackCreateDialogResult {
  participantName: string;
  pb40Token: string;
}

const dialogStyles = `
  :host { display: block; color: var(--app-surface-dark, #1e293b); }
  .dialog-heading { display: flex; gap: .9rem; padding: 1.5rem 1.5rem .4rem; }
  .dialog-icon { display: grid; place-items: center; flex: 0 0 2.6rem; height: 2.6rem; border-radius: .75rem; color: #fff; background: var(--app-primary, #145a7d); }
  h2[mat-dialog-title] { margin: 0; padding: 0; font-size: 1.2rem; font-weight: 600; }
  .dialog-heading p { margin: .25rem 0 0; color: var(--app-surface-light, #64748b); line-height: 1.45; }
  mat-dialog-content { display: grid; gap: 1rem; min-width: min(27rem, 72vw); padding-top: 1rem; }
  mat-form-field { width: 100%; }
  small { display: flex; align-items: center; gap: .35rem; margin-top: -.45rem; color: var(--app-surface-light, #64748b); line-height: 1.45; }
  small mat-icon { width: 1rem; height: 1rem; font-size: 1rem; }
  mat-dialog-actions { padding: .75rem 1.5rem 1.25rem; }
  @media (max-width: 560px) { .dialog-heading { padding: 1.1rem 1rem .25rem; } mat-dialog-content { min-width: 0; padding: .8rem 1rem; } mat-dialog-actions { padding: .6rem 1rem 1rem; } }
`;

@Component({
  selector: 'app-saas-stack-create-dialog',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  template: `
    <div class="dialog-heading">
      <span class="dialog-icon"><mat-icon>add_circle</mat-icon></span>
      <div>
        <h2 mat-dialog-title>Neue SaaS-Instanz erstellen</h2>
        <p>Der Stack wird automatisch deployed und gestartet.</p>
      </div>
    </div>
    <mat-dialog-content>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Teilnehmername</mat-label>
        <mat-icon matPrefix>business</mat-icon>
        <input matInput [(ngModel)]="participantName" maxlength="128" autocomplete="organization"
          [readonly]="!!data?.organisationName" cdkFocusInitial (keydown.enter)="submit()">
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>PB40-Token</mat-label>
        <mat-icon matPrefix>key</mat-icon>
        <input matInput type="password" [(ngModel)]="token" autocomplete="off" spellcheck="false"
          (keydown.enter)="submit()">
      </mat-form-field>
      <small><mat-icon>lock</mat-icon>Der Token wird nur für diese Anfrage verwendet und nicht gespeichert.</small>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close()">Abbrechen</button>
      <button mat-flat-button color="primary" type="button" [disabled]="!canSubmit" (click)="submit()">
        <mat-icon>rocket_launch</mat-icon>Erstellen und starten
      </button>
    </mat-dialog-actions>
  `,
  styles: [dialogStyles]
})
export class SaasStackCreateDialogComponent {
  participantName = '';
  token = '';

  constructor(
    readonly dialogRef: MatDialogRef<SaasStackCreateDialogComponent, SaasStackCreateDialogResult>,
    @Optional() @Inject(MAT_DIALOG_DATA) readonly data: { organisationName?: string } | null
  ) { this.participantName = data?.organisationName || ''; }

  get canSubmit(): boolean {
    return this.participantName.trim().length > 0 && this.token.trim().length > 0;
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.dialogRef.close({ participantName: this.participantName.trim(), pb40Token: this.token.trim() });
  }
}
